/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useReducer, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { styles } from './styles';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { usePreCreateReviewPolicyMutation, useCreateReviewPolicyMutation } from '../../../../api/preHQ';

export default function CRPoliciesAdd(props) {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [crPolicyPreCreate, setCrPolicyPreCreate] = useState({});
    const { availableStatuses = [], reviewers = [], approvers = [] } = crPolicyPreCreate;
    const [policy, setPolicy] = useState({
        name: '',
        status: 'Active',
        reviewerId: null,
        approverId: null,
    });

    const [preCreateChangeRequestPolicy, { isLoading: isPreCreateLoading }] = usePreCreateReviewPolicyMutation();
    const [createChangeRequestPolicy, { isLoading: isCreateLoading }] = useCreateReviewPolicyMutation();

    useEffect(() => {
        const fetchPreCreateData = async () => {
            try {
                const response = await preCreateChangeRequestPolicy().unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                setCrPolicyPreCreate(response);
            } catch (err) {
                if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
                else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
            }
        };

        fetchPreCreateData();
    }, [preCreateChangeRequestPolicy, enqueueSnackbar]);

    useEffect(() => {
        if (!!Object.keys(createChangeRequestPolicy).length) {
            enqueueSnackbar(createChangeRequestPolicy.message, {
                variant: 'success',
            });
            navigate('/client/reviewrequest/reviewpolicies');
        }
    }, [createChangeRequestPolicy]);

    const handleCreate = async () => {
        const { name, status } = policy;

        if (!!name && !!status) {
            const payload = {
                name,
                status,
            };

            payload['reviewerId'] = policy.reviewerId;
            payload['approverId'] = policy.approverId;

            try {
                const bodyFormData = new FormData();
                Object.keys(payload).forEach((key) => {
                    bodyFormData.append(key, payload[key]);
                });

                const response = await createChangeRequestPolicy(bodyFormData).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                if (response?.status === 'SUCCESS') {
                    enqueueSnackbar(response?.message, { variant: 'success' });
                    navigate('/client/reviewrequest/reviewpolicies');
                } else {
                    enqueueSnackbar(response?.message, { variant: 'error' });
                }
            } catch (err) {
                if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
                else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
            }
        }
    };

    const handleChange = (data, type) => {
        if (type === 'Reviewer') setPolicy({ ...policy, reviewerId: data });
        if (type === 'Approver') setPolicy({ ...policy, approverId: data });
    };

    const isDisabled = useMemo(
        () => !policy.reviewerId || !policy.approverId || !policy.status || !policy.name,
        [policy],
    );

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Add Review Policy</Typography>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card {...props}>
                    <CardContent>
                        <Grid container direction="column" spacing={2}>
                            <Grid item>
                                <Button
                                    startIcon={<ChevronLeftIcon />}
                                    variant="text"
                                    color="secondary"
                                    onClick={() => navigate('/client/reviewrequest/reviewpolicies')}
                                >
                                    Back
                                </Button>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Box sx={styles.basicInformation} component="form" noValidate autoComplete="off">
                                <Typography variant="h6" sx={styles.basicInformationText}>
                                    Basic information
                                </Typography>
                                <Box sx={styles.inputBox}>
                                    <TextField
                                        required
                                        error={policy.nameError}
                                        id="outlined-name"
                                        label="Name"
                                        sx={styles.inputs}
                                        size="small"
                                        defaultValue={policy.name}
                                        onChange={(e) =>
                                            setPolicy({
                                                ...policy,
                                                name: e.target.value,
                                            })
                                        }
                                    />
                                    <FormControl sx={styles.inputs} size="small" error={policy.statusError}>
                                        <InputLabel required id="demo-simple-select-label">
                                            Available Status
                                        </InputLabel>
                                        <Select
                                            labelId="demo-simple-select-label"
                                            id="demo-simple-select"
                                            label="Available Statuses"
                                            value={policy.status ?? ''}
                                            onChange={(e) =>
                                                setPolicy({
                                                    ...policy,
                                                    status: e.target.value,
                                                })
                                            }
                                        >
                                            {availableStatuses.map((item, index) => (
                                                <MenuItem value={item} key={index}>
                                                    {item}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Box>
                            <Box sx={styles.bottomBLine}></Box>
                            <Box sx={styles.additionalPreOfferButton}>
                                <Typography variant="h6" sx={styles.basicInformationText}>
                                    Policy Reviewer
                                </Typography>
                            </Box>
                            <Box sx={styles.employeeListsBox}>
                                <Box sx={styles.inputsAdditionalPreOfferBox}>
                                    <FormControl sx={styles.inputsAdditionalPreOffer} size="small">
                                        <InputLabel id="demo-simple-select-label">Reviewer</InputLabel>
                                        <Select
                                            labelId="demo-simple-select-label"
                                            id="demo-simple-select"
                                            defaultValue=""
                                            value={policy.reviewerId}
                                            label={'Reviewer'}
                                            onChange={(e) => handleChange(e.target.value, 'Reviewer')}
                                        >
                                            {reviewers?.map((val) => (
                                                <MenuItem key={val.id} value={val.id}>
                                                    {val.fullNameWithRole}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Box>
                            <Box sx={styles.bottomBLine}></Box>

                            <Box sx={styles.additionalPreOfferButton}>
                                <Typography variant="h6" sx={styles.basicInformationText}>
                                    Policy Approver
                                </Typography>
                            </Box>
                            <Box sx={styles.employeeListsBox}>
                                <Box sx={styles.inputsAdditionalPreOfferBox}>
                                    <FormControl sx={styles.inputsAdditionalPreOffer} size="small">
                                        <InputLabel id="demo-simple-select-label">Approver</InputLabel>
                                        <Select
                                            labelId="demo-simple-select-label"
                                            id="demo-simple-select"
                                            defaultValue=""
                                            value={policy.approverId}
                                            label={'Approver'}
                                            onChange={(e) => handleChange(e.target.value, 'Approver')}
                                        >
                                            {approvers.map((val) => (
                                                <MenuItem key={val.id} value={val.id}>
                                                    {val.fullNameWithRole}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Box>
                            <Box>
                                <Box sx={styles.bottomBLine}></Box>
                                <Box sx={styles.createButtonBox}>
                                    <Button
                                        disabled={isDisabled}
                                        color="primary"
                                        onClick={handleCreate}
                                        size="medium"
                                        sx={styles.createButton}
                                        variant="contained"
                                    >
                                        Create
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}
